import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Button,
  Link
} from "@mui/material";
import {
  LinkedIn,
  Email,
  Twitter,
  School,
  EmojiEvents,
  People
} from "@mui/icons-material";

const Team = () => {
  const theme = useTheme();

  const teamMembers = [
    {
      name: "John Smith",
      role: "CEO & Co‑founder",
      bio: "Former cleaning industry executive with 15+ years of experience building trust between homeowners and service professionals.",
      avatar: "/images/team/john-smith.jpg", // replace with actual image path
      linkedin: "#",
      email: "john@freshsweeper.com"
    },
    {
      name: "Sarah Johnson",
      role: "COO & Co‑founder",
      bio: "Operations expert passionate about creating seamless experiences for both hosts and cleaners.",
      avatar: "/images/team/sarah-johnson.jpg",
      linkedin: "#",
      email: "sarah@freshsweeper.com"
    },
    {
      name: "Michael Chen",
      role: "CTO",
      bio: "Full‑stack engineer with a background in real‑time booking systems and secure payments.",
      avatar: "/images/team/michael-chen.jpg",
      linkedin: "#",
      email: "michael@freshsweeper.com"
    },
    {
      name: "Emily Rodriguez",
      role: "Head of Cleaner Success",
      bio: "Former professional cleaner who now trains and supports our community of independent cleaners.",
      avatar: "/images/team/emily-rodriguez.jpg",
      linkedin: "#",
      email: "emily@freshsweeper.com"
    },
    {
      name: "David Kim",
      role: "Head of Host Relations",
      bio: "Dedicated to helping property owners find reliable, high‑quality cleaning services.",
      avatar: "/images/team/david-kim.jpg",
      linkedin: "#",
      email: "david@freshsweeper.com"
    },
    {
      name: "Lisa Patel",
      role: "Marketing Director",
      bio: "Creative strategist focused on building a trusted brand for both sides of the marketplace.",
      avatar: "/images/team/lisa-patel.jpg",
      linkedin: "#",
      email: "lisa@freshsweeper.com"
    }
  ];

  const advisors = [
    {
      name: "Dr. Robert Hayes",
      role: "Safety & Compliance Advisor",
      bio: "Former OSHA consultant specialising in workplace safety for cleaning services."
    },
    {
      name: "Maria Gonzales",
      role: "Sustainability Advisor",
      bio: "Expert in eco‑friendly cleaning products and green business practices."
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Company"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Our Team
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          We’re a passionate group of entrepreneurs, technologists, and cleaning industry veterans 
          dedicated to making FreshSweeper the most trusted platform for cleaning services.
        </Typography>
      </Box>

      {/* Values / Stats Bar */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3
            }}
          >
            <People sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>10k+</Typography>
            <Typography variant="body2" color="text.secondary">Cleaners on platform</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3
            }}
          >
            <EmojiEvents sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>4.98</Typography>
            <Typography variant="body2" color="text.secondary">Average rating (out of 5)</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3
            }}
          >
            <School sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
            <Typography variant="h4" fontWeight={700}>100+</Typography>
            <Typography variant="body2" color="text.secondary">Training hours available</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Team Section */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>
        Leadership & Core Team
      </Typography>
      <Grid container spacing={4}>
        {teamMembers.map((member, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
            >
              <CardMedia
                component="img"
                height="240"
                image={member.avatar}
                alt={member.name}
                sx={{ objectFit: 'cover' }}
                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x240?text=No+Image'; }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {member.name}
                </Typography>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  {member.role}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {member.bio}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <IconButton size="small" component={Link} href={member.linkedin} target="_blank" aria-label="LinkedIn">
                    <LinkedIn fontSize="small" />
                  </IconButton>
                  <IconButton size="small" component={Link} href={`mailto:${member.email}`} aria-label="Email">
                    <Email fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Advisors Section */}
      <Box sx={{ mt: 8 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>
          Advisors
        </Typography>
        <Grid container spacing={4}>
          {advisors.map((advisor, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                    {advisor.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{advisor.name}</Typography>
                    <Typography variant="subtitle2" color="primary">{advisor.role}</Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">{advisor.bio}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Join Our Team CTA */}
      <Box
        sx={{
          mt: 8,
          p: 5,
          borderRadius: 4,
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.05)
        }}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Join Our Team
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}>
          We’re always looking for talented people who share our passion for trust, quality, and innovation.
        </Typography>
        <Button variant="contained" size="large" href="/careers">View Open Positions</Button>
      </Box>
    </Container>
  );
};

export default Team;